import { CheckCircle2, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { PureDate } from '@/lib/absolute-datetime'
import { calculateTotalHours, formatHours } from '@/lib/timesheet-utils'
import { TimesheetsService } from '@/services/timesheets'
import type { HourType } from '@/types/hour-type'
import type { Project } from '@/types/project'
import type { Timesheet } from '@/types/timesheet'
import { DeleteConfirmDialog } from './DeleteConfirmDialog'
import { TimesheetLineAddDialog } from './TimesheetLineAddDialog'
import { TimesheetLineEditDialog } from './TimesheetLineEditDialog'
import { TimesheetLinesReadOnly } from './TimesheetLinesReadOnly'

interface TimesheetDetailDialogProps {
	timesheet: Timesheet | null
	open: boolean
	onOpenChange: (open: boolean) => void
	hourTypes: HourType[]
	projects: Project[]
	canManage: boolean
	onUpdate: (updated: Timesheet) => void
	onDelete?: () => void
}

export function TimesheetDetailDialog({
	timesheet,
	open,
	onOpenChange,
	hourTypes,
	projects,
	canManage,
	onUpdate,
	onDelete,
}: TimesheetDetailDialogProps) {
	const [editingLine, setEditingLine] = useState<Timesheet['lines'][0] | null>(null)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
	const [showAddLine, setShowAddLine] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const [approving, setApproving] = useState(false)

	const handleEditLine = (line: Timesheet['lines'][0]) => {
		setEditingLine(line)
	}

	const preserveTimesheetInfo = (updated: Timesheet): Timesheet => {
		if (!timesheet) return updated
		return {
			...updated,
			user: updated.user || timesheet.user,
			company: updated.company || timesheet.company,
		}
	}

	const handleLineUpdate = (updated: Timesheet) => {
		if (!timesheet) return
		const timesheetToUpdate = preserveTimesheetInfo(updated)
		onUpdate(timesheetToUpdate)
		if (editingLine) {
			const updatedLine = updated.lines.find((l) => l.id === editingLine.id)
			if (updatedLine) {
				setEditingLine(updatedLine)
			} else {
				setEditingLine(null)
			}
		} else {
			setEditingLine(null)
		}
	}

	const handleLineDelete = (updated: Timesheet) => {
		if (!timesheet) return
		const timesheetToUpdate = preserveTimesheetInfo(updated)
		onUpdate(timesheetToUpdate)
		setEditingLine(null)
	}

	const handleLineAdd = (updated: Timesheet) => {
		if (!timesheet) return
		const timesheetToUpdate = preserveTimesheetInfo(updated)
		onUpdate(timesheetToUpdate)
	}

	const handleDeleteTimesheet = async () => {
		if (!timesheet || !timesheet.company) return

		try {
			setDeleting(true)
			setShowDeleteConfirm(false)

			await TimesheetsService.deleteTimesheet(timesheet.id, timesheet.company.id)

			toast.success('Registro horario eliminado correctamente')
			onOpenChange(false)

			if (onDelete) {
				onDelete()
			}
		} catch (err) {
			console.error(err)
			toast.error(err instanceof Error ? err.message : 'Error al eliminar el registro horario')
		} finally {
			setDeleting(false)
		}
	}

	const handleApproveAll = async () => {
		if (!timesheet) return

		const unapprovedLines = timesheet.lines.filter((l) => !l.approved)
		if (unapprovedLines.length === 0) {
			toast.info('Todas las líneas ya están aprobadas')
			return
		}

		try {
			setApproving(true)

			let lastUpdated: Timesheet | null = null
			for (const line of unapprovedLines) {
				lastUpdated = await TimesheetsService.approveTimesheetLine(line.id)
			}

			if (lastUpdated) {
				const timesheetToUpdate = preserveTimesheetInfo(lastUpdated)
				onUpdate(timesheetToUpdate)
			}

			toast.success(`${unapprovedLines.length} línea${unapprovedLines.length > 1 ? 's' : ''} aprobada${unapprovedLines.length > 1 ? 's' : ''} correctamente`)
		} catch (err) {
			console.error(err)
			toast.error(err instanceof Error ? err.message : 'Error al aprobar las líneas')
		} finally {
			setApproving(false)
		}
	}

	if (!timesheet) return null

	// Validación defensiva para evitar errores si falta información
	if (!timesheet.user || !timesheet.company) {
		return null
	}

	const hasUnapprovedLines = timesheet.lines.some((l) => !l.approved)

	return (
		<>
			<Dialog
				open={open}
				onOpenChange={(open) => {
					onOpenChange(open)
					if (!open) {
						setEditingLine(null)
					}
				}}
			>
				<DialogContent className="max-w-[90vw] sm:max-w-[85vw] w-full max-h-[95vh] overflow-y-auto flex flex-col">
					<DialogHeader>
						<DialogTitle>Detalles del registro horario</DialogTitle>
						<DialogDescription>
							Información completa del registro horario seleccionado
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-6 py-4">
						{/* Información general */}
						<div className="grid gap-4 md:grid-cols-2">
							<div className="space-y-2">
								<Label className="text-sm font-medium text-muted-foreground">Empleado</Label>
								<p className="text-base font-medium">{timesheet.user.name}</p>
								{timesheet.user.code && (
									<p className="text-sm text-muted-foreground">Código: {timesheet.user.code}</p>
								)}
							</div>
							<div className="space-y-2">
								<Label className="text-sm font-medium text-muted-foreground">Fecha</Label>
								<p className="text-base font-medium">{new PureDate(timesheet.date).toSpanishDate()}</p>
							</div>
						</div>

						{/* Líneas del registro */}
						<div className="space-y-4">
							<div className="flex items-center justify-between">
								<Label className="text-base font-semibold">Líneas del registro</Label>
								{canManage && (
									<div className="flex gap-2">
										{hasUnapprovedLines && timesheet.lines.length > 0 && (
											<Button
												variant="outline"
												size="sm"
												onClick={handleApproveAll}
												disabled={approving}
											>
												<CheckCircle2 className="h-4 w-4 mr-2" />
												{approving ? 'Aprobando...' : 'Aprobar todas'}
											</Button>
										)}
										<Button
											variant="outline"
											size="sm"
											onClick={() => setShowAddLine(true)}
										>
											<Plus className="h-4 w-4 mr-2" />
											Añadir línea
										</Button>
									</div>
								)}
							</div>
							{timesheet.lines.length === 0 ? (
								<p className="text-sm text-muted-foreground">No hay líneas registradas</p>
							) : (
								<TimesheetLinesReadOnly
									lines={timesheet.lines}
									canManage={canManage}
									onEditLine={handleEditLine}
								/>
							)}
						</div>

						{/* footer */}
						<div className="flex justify-end">
							<Label className="text-sm font-medium text-muted-foreground">
								Total de horas: {formatHours(calculateTotalHours(timesheet.lines))}
							</Label>
						</div>
					</div>
					<DialogFooter>
						<div className="flex w-full items-center justify-between">
							{canManage ? (
								<Button
									variant="destructive"
									onClick={() => setShowDeleteConfirm(true)}
									disabled={deleting}
								>
									<Trash2 className="h-4 w-4 mr-2" />
									{deleting ? 'Eliminando...' : 'Eliminar'}
								</Button>
							) : (
								<div />
							)}
							<Button variant="outline" onClick={() => onOpenChange(false)}>
								Cerrar
							</Button>
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<DeleteConfirmDialog
				open={showDeleteConfirm}
				onOpenChange={setShowDeleteConfirm}
				title="⚠️ Eliminar registro horario"
				description="Esta acción es irreversible. Se eliminará permanentemente este registro horario junto con todas sus líneas asociadas. Los datos eliminados no podrán recuperarse. ¿Deseas continuar?"
				onConfirm={handleDeleteTimesheet}
			/>
			{canManage && timesheet && (
				<TimesheetLineAddDialog
					timesheet={timesheet}
					open={showAddLine}
					onOpenChange={setShowAddLine}
					hourTypes={hourTypes}
					projects={projects}
					onAdd={handleLineAdd}
				/>
			)}
			{editingLine && timesheet && (
				<TimesheetLineEditDialog
					timesheet={timesheet}
					line={editingLine}
					open={!!editingLine}
					onOpenChange={(open) => !open && setEditingLine(null)}
					hourTypes={hourTypes}
					projects={projects}
					onUpdate={handleLineUpdate}
					onDelete={handleLineDelete}
				/>
			)}
		</>
	)
}
